import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SNOOZE_DAYS, type FollowUpItem } from "../../shared/followup";
import { eventCardHref } from "../../shared/inquiry";
import { hasPermission } from "../../shared/permissions";
import { evaluationLabel } from "../../shared/tasting";
import { api } from "../api";
import { useSession } from "../App";
import { followUpsChanged, useFollowUps } from "../followups";
import { Banner, messageOf } from "./ui";

export function shortDate(iso: string | null): string {
  if (!iso) return "—";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

export function EvaluationBadge({ code }: { code: FollowUpItem["evaluation"] }) {
  const label = code ? evaluationLabel(code) : "χωρίς EVLTN";
  return <span className={`ev-badge ev-${code ?? "none"}`} title={label}>{code ?? "—"}</span>;
}

function dueText(item: FollowUpItem, today: string): string {
  if (item.overdue_days > 0) return `καθυστερεί ${item.overdue_days} ${item.overdue_days === 1 ? "μέρα" : "μέρες"}`;
  if (item.follow_up === today) return "σήμερα";
  return shortDate(item.follow_up);
}

export function FollowUpPage() {
  const session = useSession();
  const { list, error, reload } = useFollowUps(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const canWrite = hasPermission(session.user.role, session.user.permissions, "events.write");

  async function act(item: FollowUpItem, path: string, body?: unknown) {
    setBusy(item.id);
    setActionError(null);
    try {
      const result = await api<{ follow_up: string | null }>(`/api/followups/${encodeURIComponent(item.id)}/${path}`, { method: "POST", body: JSON.stringify(body ?? {}) });
      setNote(`${item.name}: ${result.follow_up ? `επόμενο follow-up ${shortDate(result.follow_up)}` : "χωρίς επόμενο follow-up (το EVLTN δεν έχει διάστημα)"}.`);
      reload();
      followUpsChanged();
    } catch (reason) {
      setActionError(messageOf(reason));
    } finally {
      setBusy(null);
    }
  }

  const rows = (items: FollowUpItem[]) => (
    <div className="fu-list">
      {items.map((item) => (
        <FollowUpRow key={item.id} item={item} today={list!.today} busy={busy === item.id} canWrite={canWrite} onDone={() => void act(item, "done")} onSnooze={(days) => void act(item, "snooze", { days })} />
      ))}
    </div>
  );

  return (
    <section className="panel followup-page">
      <h1>Follow-up</h1>
      <p className="lede">Τηλέφωνα της ημέρας: πρώτα όσα καθυστερούν, με σειρά ενδιαφέροντος (TP → HP → MP → LP → VLP → άλλο).</p>
      <Banner error={error ?? actionError} note={note} />
      {!list && !error ? <p>Φόρτωση…</p> : null}
      {list ? (
        <>
          <h2 className="fu-heading">Σήμερα & εκπρόθεσμα <span className={list.due.length ? "fu-count hot" : "fu-count"}>{list.due.length}</span></h2>
          {list.due.length ? rows(list.due) : <p className="muted">Κανένα follow-up για σήμερα.</p>}
          <h2 className="fu-heading">Επόμενες 7 μέρες <span className="fu-count">{list.upcoming.length}</span></h2>
          {list.upcoming.length ? rows(list.upcoming) : <p className="muted">Τίποτα τις επόμενες 7 μέρες.</p>}
        </>
      ) : null}
    </section>
  );
}

function FollowUpRow({ item, today, busy, canWrite, onDone, onSnooze }: { item: FollowUpItem; today: string; busy: boolean; canWrite: boolean; onDone: () => void; onSnooze: (days: number) => void }) {
  const navigate = useNavigate();
  const href = eventCardHref(item.id);
  const stop = (event: { stopPropagation: () => void }) => event.stopPropagation();
  return (
    <div
      className={item.overdue_days > 0 ? "fu-row overdue" : "fu-row"}
      role="link"
      tabIndex={0}
      onClick={() => navigate(href)}
      onKeyDown={(event) => {
        if (event.key === "Enter") navigate(href);
      }}
    >
      <div className="fu-main">
        <div className="fu-title">
          <EvaluationBadge code={item.evaluation} />
          <strong>{item.name}</strong>
          <span className={item.overdue_days > 0 ? "fu-due late" : "fu-due"}>{dueText(item, today)}{item.computed ? " · υπολογισμένο" : ""}</span>
        </div>
        <div className="fu-meta">
          <span>Εκδήλωση {shortDate(item.event_date)}</span>
          <span>{item.venue ?? "χωρίς χώρο"}</span>
          <span>{item.guests != null ? `${item.guests} άτομα` : "? άτομα"}</span>
          {item.phone ? <a href={`tel:${item.phone}`} onClick={stop}>{item.phone}</a> : <span>χωρίς τηλέφωνο</span>}
          <span>Τελευταία επαφή {shortDate(item.last_contact)}</span>
        </div>
      </div>
      {canWrite ? (
        <div className="fu-actions" onClick={stop} onKeyDown={stop}>
          <button type="button" disabled={busy} onClick={onDone} title="Επόμενο follow-up = σήμερα + διάστημα EVLTN">✓ Έγινε</button>
          <select aria-label="Αναβολή" disabled={busy} value="" onChange={(event) => event.target.value && onSnooze(Number(event.target.value))}>
            <option value="">Αναβολή…</option>
            {SNOOZE_DAYS.map((days) => (
              <option key={days} value={days}>{days === 1 ? "Αύριο" : `+${days} μέρες`}</option>
            ))}
          </select>
        </div>
      ) : null}
    </div>
  );
}
